import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import {
  BehaviorSubject,
  Observable,
  catchError,
  forkJoin,
  map,
  of,
  switchMap,
} from 'rxjs';
import { HttpPagedResponse } from '../models/http/http-paged-response.model';
import {
  IHttpPagedResponse,
  IHttpSingleDataResponse,
  ToastService,
  IOrganizationWhiteLabel,
} from '@irembo-andela/irembogov3-common';
import { KeycloakService } from 'keycloak-angular';
import { IOrganizationUserCreationResponse } from '../models/organization/organization-user-creation-response.model';
import { IOrganizationUserCreationRequest } from '../models/organization/organization-user-creation-request.model';
import { IOrganizationUserResponse } from '../models/organization/organization-user-response.model';
import { IBaseResponseBody } from '../models/http/base-response.model';
import { HttpUnpagedResponse } from '../models/http/http-unpaged-response.model';
import { AuthService } from './auth.service';
import { IOrganizationPublic } from '../models/organization/organization-public-response.model';
import { UnassignRoleRequest } from '../models/auth/unassign-role-request.model';
import { IUserByRolesAndScopeResponse } from '../models/auth/user-by-roles-and-scope-response.model';
import { IOrganizationDetails } from '../models/organization/organization-response.model';
import { IUserResponse } from '../models/auth/user-response.model';
import {
  ISuspendLabel,
  IWhiteLabelRequest,
  whiteLabelUpdateData,
} from '../models/organization/organization-white-label.model';

@Injectable({
  providedIn: 'root',
})
export class OrganizationService {
  selectedOrganization: BehaviorSubject<IOrganizationPublic | undefined> =
    new BehaviorSubject<IOrganizationPublic | undefined>(undefined);
  loadedOrganizations: BehaviorSubject<IOrganizationPublic[]> =
    new BehaviorSubject<IOrganizationPublic[]>([]);

  constructor(
    private http: HttpClient,
    private keyCloakService: KeycloakService,
    private toastService: ToastService,
    private authService: AuthService
  ) {
    this.handleAuth();
  }

  private async handleAuth() {
    if (await this.keyCloakService.isLoggedIn()) {
      const profile = await this.keyCloakService.loadUserProfile(true);
      this.getOrganizationByUser(profile.id);
    }
  }

  getOrganizationByUser(userId?: string) {
    this.authService.getUserDetails(userId ?? '').subscribe({
      next: value => {
        const scopeIds: string[] = [];

        value.data.userRoles.forEach(role => {
          role.scopes.forEach(scope => {
            if (
              scope.scopeType === 'ORGANIZATION' &&
              scope.scopeId !== null &&
              !scopeIds.includes(scope.scopeId)
            ) {
              scopeIds.push(scope.scopeId);
            }
          });
        });

        const organizations: IOrganizationPublic[] = [];

        if (scopeIds.length > 0) {
          const logoRequests = scopeIds.map(element => {
            return this.getOrganizationById(element).pipe(
              switchMap(organization => {
                return this.getImageUrlFromBlob(organization.data.logo).pipe(
                  map(imageUrl => {
                    return { ...organization.data, logo: imageUrl };
                  }),
                  catchError(() => {
                    return of({ ...organization.data, logo: '' });
                  })
                );
              })
            );
          });

          forkJoin(logoRequests).subscribe({
            next: (orgsWithLogos: IOrganizationPublic[]) => {
              organizations.push(...orgsWithLogos);
              if (!this.selectedOrganization.value) {
                this.selectedOrganization.next(orgsWithLogos[0]);
              }
              this.loadedOrganizations.next(organizations);
            },
            error: error => {
              console.error('Error fetching organizations:', error);
            },
          });
        }
      },
      error: err => {
        this.toastService.show({ body: err.error.message, type: 'error' });
      },
    });
  }

  getImageUrlFromBlob(fileName: string): Observable<string> {
    return new Observable<string>(observer => {
      this.getOrganizationLogo(fileName).subscribe({
        next: (blob: Blob) => {
          const imageUrl = URL.createObjectURL(blob);
          observer.next(imageUrl);
          observer.complete();
        },
        error: error => {
          observer.error(error);
        },
      });
    });
  }

  getOrganizationById(
    organizationId: string
  ): Observable<IBaseResponseBody<IOrganizationPublic>> {
    const url = `${environment.apiGatewayBaseUrl}/admin/v1/public/organizations/${organizationId}`;
    return this.http.get<IBaseResponseBody<IOrganizationPublic>>(url);
  }

  createOrganizationUser(
    userRequest: IOrganizationUserCreationRequest,
    roleName: string
  ): Observable<HttpUnpagedResponse<IOrganizationUserCreationResponse>> {
    const organizationId = this.selectedOrganization.value?.id;
    const role: any = {
      role: roleName,
      scopeType: 'ORGANIZATION',
      scopeId: `${organizationId}`,
    };
    userRequest.roles.push(role);
    userRequest.clientId = environment.authClientId;
    const url = `${environment.apiGatewayBaseUrl}/auth/v1/create-organization-user`;
    return this.http.post<
      HttpUnpagedResponse<IOrganizationUserCreationResponse>
    >(url, userRequest);
  }

  getOrganizationUsers(
    page: number,
    pageSize: number,
    role: string,
    searchTerm: string,
    default_roles = 'ROLE_ORGANIZATION_ADMIN,ROLE_ORGANIZATION_EXECUTIVE,ROLE_ORGANIZATION_SUPPORT'
  ): Observable<HttpPagedResponse<IUserByRolesAndScopeResponse>> {
    const organizationId = this.selectedOrganization.value?.id;
    const url = `${
      environment.apiGatewayBaseUrl
    }/auth/v1/user-role/by-role-and-scope?size=${pageSize}&page=${page}&${
      searchTerm ? 'searchTerm=' + searchTerm : ''
    }`;

    const headers = new HttpHeaders({
      roles: role ? role : default_roles,
      scopeId: organizationId ?? '',
      scopeType: 'ORGANIZATION',
    });
    return this.http.get<HttpPagedResponse<IUserByRolesAndScopeResponse>>(url, {
      headers,
    });
  }

  deactivateOrganizationUser(
    userId: string,
    roleName: string
  ): Observable<IBaseResponseBody<IOrganizationUserResponse>> {
    const unassignRoleRequest: UnassignRoleRequest = {
      userId,
      roleName,
      clientId: environment.authClientId,
      userType: 'OTHER',
    };
    const url = `${environment.apiGatewayBaseUrl}/auth/v1/user/unassign-role`;
    return this.http.patch<IBaseResponseBody<IOrganizationUserResponse>>(
      url,
      unassignRoleRequest
    );
  }

  getOrganizationLogo(imageName: string): Observable<Blob> {
    const url = `${environment.apiGatewayBaseUrl}/admin/v1/file-manager?fileName=${imageName}`;
    return this.http.get(url, { responseType: 'blob' });
  }

  activateWhiteLabel(organizationId: string) {
    const url = `${environment.apiGatewayBaseUrl}/admin/v1/organizations/${organizationId}/requests`;
    return this.http.post<IHttpSingleDataResponse<Record<string, unknown>>>(
      url,
      null
    );
  }

  getOrganizationWhiteLabels(organizationId: string, queryParams?: string) {
    const url = `${environment.apiGatewayBaseUrl}/admin/v1/organizations/${organizationId}/white-labels?${queryParams}`;
    return this.http.get<IHttpPagedResponse<IOrganizationWhiteLabel>>(url);
  }

  getOrganizationWhiteLabelsRequest(organizationId: string) {
    const url = `${environment.apiGatewayBaseUrl}/admin/v1/organizations/${organizationId}/requests`;
    return this.http.get<IHttpPagedResponse<IWhiteLabelRequest>>(url);
  }

  getOrganizationDetailsById(organizationId: string) {
    const url = `${environment.apiGatewayBaseUrl}/admin/v1/organizations/${organizationId}`;
    return this.http.get<IHttpSingleDataResponse<IOrganizationDetails>>(url);
  }

  private getUserById(
    userId: string
  ): Observable<IBaseResponseBody<IUserResponse>> {
    const headers = new HttpHeaders({
      userId,
      userType: 'OTHER',
    });
    const url = `${environment.apiGatewayBaseUrl}/auth/v1/user/organization-admin-user-by-id`;
    return this.http.get<IBaseResponseBody<IUserResponse>>(url, { headers });
  }

  getOrganizationUserDetails(organizationId: string) {
    const url = `${environment.apiGatewayBaseUrl}/admin/v1/organizations/${organizationId}`;

    return this.http
      .get<IHttpSingleDataResponse<IOrganizationDetails>>(url)
      .pipe(
        switchMap(org => {
          const createdBy = org.data.createdById;
          const approverId = org.data.approvedById;

          const creator$ = this.getUserById(createdBy);
          const approver$ = this.getUserById(approverId);

          return forkJoin([creator$, approver$]).pipe(
            map(([creator, approver]) => ({
              orgData: org,
              createdBy: creator.data,
              approvedBy: approver.data,
            }))
          );
        })
      );
  }

  getWhiteLabelById(whiteLabelId: string) {
    const url = `${environment.apiGatewayBaseUrl}/admin/v1/organizations/white-labels/${whiteLabelId}`;
    return this.http.get<IHttpSingleDataResponse<IOrganizationWhiteLabel>>(url);
  }

  updateWhitelabel(whiteLabelId: string, data: whiteLabelUpdateData) {
    const url = `${environment.apiGatewayBaseUrl}/admin/v1/organizations/white-labels/${whiteLabelId}`;
    return this.http.put<IHttpSingleDataResponse<IOrganizationWhiteLabel>>(
      url,
      data
    );
  }

  publishOrganizationWhiteLabel(id: string): Observable<any> {
    return this.http.post(
      `${environment.apiGatewayBaseUrl}/admin/v1/organizations/white-labels/${id}/publish`,
      {}
    );
  }

  suspendOrganizationWhiteLabel(
    id: string,
    payload: ISuspendLabel
  ): Observable<any> {
    return this.http.post(
      `${environment.apiGatewayBaseUrl}/admin/v1/organizations/white-labels/${id}/suspend`,
      payload
    );
  }
}
